import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileText, Plus, Edit, Trash2, Eye, Sparkles } from 'lucide-react';
import { V1Layout } from '../../components/v1/V1Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../ui/card';
import { Button } from '../../../../ui/button';
import { Badge } from '../../../../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../../../../ui/dialog';
import { Input } from '../../../../ui/input';
import { Label } from '../../../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../../../ui/tabs';
import { epicengageSupabase } from '../../../../../integrations/supabase/client';
import { useMerchant } from '../../../../../hooks/useMerchant';
import { toast } from 'sonner';
import type { EmailTemplateV2, TemplateType, EditorType, TemplateOrigin } from '../../../../../types/epicengage';
import { format } from 'date-fns';

export default function Templates() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { merchant } = useMerchant();
  const [templates, setTemplates] = useState<EmailTemplateV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteBlockedTemplate, setDeleteBlockedTemplate] = useState<EmailTemplateV2 | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplateV2 | null>(null);
  const [activeType, setActiveType] = useState<TemplateType | 'ALL'>('ALL');
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    type: 'MANUAL' as TemplateType,
    editor_type: 'STRUCTURED' as EditorType,
    subject: ''
  });

  const fetchTemplates = async () => {
    if (!merchant) return;

    let query = epicengageSupabase
      .from('email_templates_v2')
      .select('*')
      .eq('merchant_id', merchant.id)
      .order('updated_at', { ascending: false });

    if (activeType !== 'ALL') {
      query = query.eq('type', activeType);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching templates:', error);
      return;
    }

    setTemplates((data || []) as EmailTemplateV2[]);
    setLoading(false);
  };

  useEffect(() => {
    fetchTemplates();
  }, [merchant, activeType]);

  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      setShowCreateDialog(true);
    }
    const typeParam = searchParams.get('type');
    if (typeParam && ['AUTOMATION', 'EVENT', 'MANUAL'].includes(typeParam)) {
      setNewTemplate(t => ({ ...t, type: typeParam as TemplateType }));
    }
  }, [searchParams]);

  const handleCreateTemplate = async () => {
    if (!merchant || !newTemplate.name) return;

    const { data, error } = await epicengageSupabase
      .from('email_templates_v2')
      .insert({
        merchant_id: merchant.id,
        name: newTemplate.name,
        type: newTemplate.type,
        editor_type: newTemplate.editor_type,
        subject: newTemplate.subject,
        origin: 'MANUAL'
      })
      .select()
      .single();

    if (error) {
      toast.error('Failed to create template: ' + error.message);
      return;
    }

    toast.success('Template created');
    setShowCreateDialog(false);
    setNewTemplate({ name: '', type: 'MANUAL', editor_type: 'STRUCTURED', subject: '' });

    // Navigate to editor
    if (newTemplate.editor_type === 'GRAPESJS') {
      navigate(`/clientpanel/enage/templates/${data.id}/grapesjs`);
    } else {
      navigate(`/clientpanel/enage/templates/${data.id}/edit`);
    }
  };

  const checkTemplateInUse = async (templateId: string): Promise<boolean> => {
    // Check if this template is linked to any active sequence step
    const { data, error } = await epicengageSupabase
      .from('sequence_steps')
      .select(`
        id,
        sequences!inner (
          enabled
        )
      `)
      .eq('template_id', templateId);

    if (error) {
      console.error('Error checking template usage:', error);
      return false;
    }

    // Check if any of the linked sequences are enabled
    return data?.some((step: any) => step.sequences?.enabled) || false;
  };

  const handleDeleteTemplate = async (template: EmailTemplateV2) => {
    // Check if it's a system-generated template in use
    if (template.origin === 'SYSTEM_GENERATED') {
      const inUse = await checkTemplateInUse(template.id);
      if (inUse) {
        setDeleteBlockedTemplate(template);
        return;
      }
    }

    //if (!confirm(`Delete "${template.name}"? This cannot be undone.`)) return;

    const { error } = await epicengageSupabase
      .from('email_templates_v2')
      .delete()
      .eq('id', template.id);

    if (error) {
      toast.error('Failed to delete template');
      return;
    }

    toast.success('Template deleted');
    fetchTemplates();
  };

  const handleDuplicateTemplate = async (template: EmailTemplateV2) => {
    if (!merchant) return;

    const { data, error } = await epicengageSupabase
      .from('email_templates_v2')
      .insert({
        merchant_id: merchant.id,
        name: `${template.name} (Copy)`,
        type: template.type,
        editor_type: template.editor_type,
        subject: template.subject,
        body_json: template.body_json,
        grapesjs_json: template.grapesjs_json,
        html_cache: template.html_cache,
        origin: 'MANUAL' // Duplicates are always manual
      })
      .select()
      .single();

    if (error) {
      toast.error('Failed to duplicate template');
      return;
    }

    toast.success('Template duplicated');
    setDeleteBlockedTemplate(null);
    fetchTemplates();

    // Navigate to the new template
    if (template.editor_type === 'GRAPESJS') {
      navigate(`/clientpanel/enage/templates/${data.id}/grapesjs`);
    } else {
      navigate(`/clientpanel/enage/templates/${data.id}/edit`);
    }
  };

  const getTypeBadge = (type: TemplateType) => {
    switch (type) {
      case 'AUTOMATION':
        return <Badge variant="secondary">Automation</Badge>;
      case 'EVENT':
        return <Badge variant="outline">Event</Badge>;
      case 'MANUAL':
        return <Badge>Manual</Badge>;
    }
  };

  return (
    <V1Layout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <FileText className="h-8 w-8" />
              Templates
            </h1>
            <p className="text-muted-foreground mt-1">
              Create and manage your email templates
            </p>
          </div>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Template</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Template Name *</Label>
                  <Input
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate(t => ({ ...t, name: e.target.value }))}
                    placeholder="Welcome Email"
                  />
                </div>
                <div>
                  <Label>Subject Line</Label>
                  <Input
                    value={newTemplate.subject}
                    onChange={(e) => setNewTemplate(t => ({ ...t, subject: e.target.value }))}
                    placeholder="Welcome to our community!"
                  />
                </div>
                <div>
                  <Label>Type</Label>
                  <Select
                    value={newTemplate.type}
                    onValueChange={(v) => setNewTemplate(t => ({ ...t, type: v as TemplateType }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MANUAL">Manual (for blasts)</SelectItem>
                      <SelectItem value="AUTOMATION">Automation (for sequences)</SelectItem>
                      <SelectItem value="EVENT">Event (for holidays)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Editor Type</Label>
                  <Select
                    value={newTemplate.editor_type}
                    onValueChange={(v) => setNewTemplate(t => ({ ...t, editor_type: v as EditorType }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="STRUCTURED">Simple Editor</SelectItem>
                      <SelectItem value="GRAPESJS">Advanced Editor (GrapesJS)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button onClick={handleCreateTemplate} className="w-full">
                  Create Template
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Type Filter */}
        <Tabs value={activeType} onValueChange={(v) => setActiveType(v as any)} className="mb-6">
          <TabsList>
            <TabsTrigger value="ALL">All</TabsTrigger>
            <TabsTrigger value="MANUAL">Manual</TabsTrigger>
            <TabsTrigger value="AUTOMATION">Automation</TabsTrigger>
            <TabsTrigger value="EVENT">Event</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Templates Grid */}
        {loading ? (
          <p className="text-muted-foreground">Loading templates...</p>
        ) : templates.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No templates yet</h3>
              <p className="text-muted-foreground mb-4">Create your first email template to get started</p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Template
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => (
              <Card key={template.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg flex items-center gap-2">
                        {template.name}
                        {template.origin === 'SYSTEM_GENERATED' && (
                          <Badge variant="outline" className="text-xs font-normal gap-1">
                            <Sparkles className="h-3 w-3" />
                            Auto-created
                          </Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="truncate mt-1">
                        {template.subject || 'No subject'}
                      </CardDescription>
                    </div>
                    {getTypeBadge(template.type)}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-xs text-muted-foreground mb-4">
                    Updated {format(new Date(template.updated_at), 'MMM d, yyyy')}
                    <span className="mx-2">•</span>
                    {template.editor_type === 'GRAPESJS' ? 'Advanced Editor' : 'Simple Editor'}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => {
                        if (template.editor_type === 'GRAPESJS') {
                          navigate(`/clientpanel/enage/templates/${template.id}/grapesjs`);
                        } else {
                          navigate(`/clientpanel/enage//templates/${template.id}/edit`);
                        }
                      }}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setPreviewTemplate(template)}
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDeleteTemplate(template)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Delete Blocked Dialog */}
        <Dialog open={!!deleteBlockedTemplate} onOpenChange={() => setDeleteBlockedTemplate(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cannot Delete Template</DialogTitle>
              <DialogDescription>
                This template is used in an active automation and cannot be deleted.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground">
                To make changes, you can duplicate this template and edit the copy instead.
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteBlockedTemplate(null)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => deleteBlockedTemplate && handleDuplicateTemplate(deleteBlockedTemplate)}
                  className="flex-1"
                >
                  Duplicate Template
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Preview Modal */}
        <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>{previewTemplate?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Subject</p>
                <p className="text-lg">{previewTemplate?.subject}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Body</p>
                {previewTemplate?.html_cache ? (
                  <div 
                    className="border rounded-lg p-4 bg-white"
                    dangerouslySetInnerHTML={{ __html: previewTemplate.html_cache }}
                  />
                ) : previewTemplate?.body_json?.text ? (
                  <div className="border rounded-lg p-4 bg-white whitespace-pre-wrap">
                    {previewTemplate.body_json.text}
                  </div>
                ) : (
                  <p className="text-muted-foreground italic">No content</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </V1Layout>
  );
}